import {
  Package, Search, Filter, X 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  FormModal, FormField, FormInput, FormSelect, FormTextarea 
} from '@/components/ui/form-modal';
import {
  CatalogItemCard, ProductDetailModal 
} from '@/features/catalog';
import type { CatalogItem } from '@/features/catalog';
import {
  CATEGORIES, UNIT_OPTIONS 
} from './catalog-constants';

interface ItemFormData {
  name: string;
  sku: string;
  category: string;
  description: string;
  unit: string;
  unitCost: string;
}

interface ItemsViewProps {
  readonly items: CatalogItem[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly categoryCounts: Record<string, number>;
  readonly searchQuery: string;
  readonly setSearchQuery: (query: string) => void;
  readonly selectedCategory: string;
  readonly setSelectedCategory: (category: string) => void;
  readonly selectedItem: CatalogItem | null;
  readonly setSelectedItem: (item: CatalogItem | null) => void;
  readonly showAddModal: boolean;
  readonly setShowAddModal: (show: boolean) => void;
  readonly editingItem: string | null;
  readonly formData: ItemFormData;
  readonly setFormData: (data: ItemFormData) => void;
  readonly onAddItem: () => void;
  readonly onEditItem: (item: CatalogItem) => void;
  readonly onSaveItem: () => void;
  readonly onRefetch: () => void;
  readonly onAddSubstitute?: (item: CatalogItem) => void;
  readonly onAddRelatedItem?: (item: CatalogItem) => void;
  readonly onUploadFile?: (item: CatalogItem) => void;
}

export function ItemsView({
  items,
  isLoading,
  error,
  categoryCounts,
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  selectedItem,
  setSelectedItem,
  showAddModal,
  setShowAddModal,
  editingItem,
  formData,
  setFormData,
  onAddItem,
  onEditItem,
  onSaveItem,
  onRefetch,
  onAddSubstitute,
  onAddRelatedItem,
  onUploadFile,
}: ItemsViewProps) {
  const categoryOptions = CATEGORIES.filter((c) => c !== 'All').map((c) => ({
    value: c,
    label: c 
  }));

  const handleItemClick = (item: CatalogItem) => {
    setSelectedItem(item);
  };

  const handleEditFromModal = () => {
    if (selectedItem) {
      setSelectedItem(null);
      onEditItem(selectedItem);
    }
  };

  const handleAddSubstituteFromModal = () => {
    if (selectedItem && onAddSubstitute) {
      onAddSubstitute(selectedItem);
    }
  };

  const handleAddRelatedItem = () => {
    if (selectedItem && onAddRelatedItem) {
      onAddRelatedItem(selectedItem);
    }
  };

  const handleUploadFile = () => {
    if (selectedItem && onUploadFile) {
      onUploadFile(selectedItem);
    }
  };

  return (
    <>
      {/* Product Detail Modal */}
      {selectedItem && (
        <ProductDetailModal
          item={selectedItem}
          isOpen={Boolean(selectedItem)}
          onClose={() => setSelectedItem(null)}
          onEdit={handleEditFromModal}
          onAddSubstitute={handleAddSubstituteFromModal}
          onAddRelatedItem={handleAddRelatedItem}
          onUploadFile={handleUploadFile}
        />
      )}

      {/* Add Item Button */}
      <div className="flex justify-end">
        <Button onClick={onAddItem}>Add Item</Button>
      </div>

      <FormModal
        title={editingItem ? 'Edit Catalog Item' : 'Add Catalog Item'}
        description={editingItem ? 'Update item details' : 'Add a new item to the master catalog'}
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={onSaveItem}
        saveLabel={editingItem ? 'Update' : 'Create'}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="Name" required>
            <FormInput
              value={formData.name}
              onChange={(e) => setFormData({
                ...formData,
                name: e.target.value 
              })}
              placeholder="e.g., Atlantic Salmon Fillet"
            />
          </FormField>
          <FormField label="SKU" required>
            <FormInput
              value={formData.sku}
              onChange={(e) => setFormData({
                ...formData,
                sku: e.target.value 
              })}
              placeholder="e.g., SEA-SAL-001"
            />
          </FormField>
          <FormField label="Category" required>
            <FormSelect
              value={formData.category}
              onChange={(e) => setFormData({
                ...formData,
                category: e.target.value 
              })}
              placeholder="Select category..."
              options={categoryOptions}
            />
          </FormField>
          <FormField label="Unit">
            <FormSelect
              value={formData.unit}
              onChange={(e) => setFormData({
                ...formData,
                unit: e.target.value 
              })}
              placeholder="Select unit..."
              options={UNIT_OPTIONS}
            />
          </FormField>
          <FormField label="Unit Cost ($)">
            <FormInput
              type="number"
              step="0.01"
              min="0"
              value={formData.unitCost}
              onChange={(e) => setFormData({
                ...formData,
                unitCost: e.target.value 
              })}
              placeholder="0.00"
            />
          </FormField>
        </div>
        <FormField label="Description">
          <FormTextarea
            value={formData.description}
            onChange={(e) => setFormData({
              ...formData,
              description: e.target.value 
            })}
            placeholder="Optional description..."
            rows={2}
          />
        </FormField>
      </FormModal>

      <SummaryCards categoryCounts={categoryCounts} itemsCount={items.length} searchQuery={searchQuery} selectedCategory={selectedCategory} />

      <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />

      <CategoryFilters selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} categoryCounts={categoryCounts} />

      <ItemsGrid
        items={items}
        isLoading={isLoading}
        error={error}
        searchQuery={searchQuery}
        selectedCategory={selectedCategory}
        onItemClick={handleItemClick}
        onRefetch={onRefetch}
      />
    </>
  );
}

function SummaryCards({
  categoryCounts,
  itemsCount,
  searchQuery,
  selectedCategory,
}: {
  readonly categoryCounts: Record<string, number>;
  readonly itemsCount: number;
  readonly searchQuery: string;
  readonly selectedCategory: string;
}) {
  const activeFilters = (selectedCategory === 'All' ? 0 : 1) + (searchQuery ? 1 : 0);

  return (
    <div className="grid gap-4 md:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Total Items</CardTitle>
          <Package className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{categoryCounts.All ?? 0}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Categories</CardTitle>
          <Filter className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{Object.keys(categoryCounts).length - 1}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Filtered Results</CardTitle>
          <Search className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{itemsCount}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Active Filters</CardTitle>
          <Filter className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{activeFilters}</div>
        </CardContent>
      </Card>
    </div>
  );
}

function SearchBar({
  searchQuery,
  setSearchQuery,
}: {
  readonly searchQuery: string;
  readonly setSearchQuery: (query: string) => void;
}) {
  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-center">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by name or SKU..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function CategoryFilters({
  selectedCategory,
  setSelectedCategory,
  categoryCounts,
}: {
  readonly selectedCategory: string;
  readonly setSelectedCategory: (category: string) => void;
  readonly categoryCounts: Record<string, number>;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {CATEGORIES.map((category) => (
        <Button
          key={category}
          variant={selectedCategory === category ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedCategory(category)}
        >
          {category}
          {category in categoryCounts && (
            <span className="ml-1 text-xs opacity-70">({categoryCounts[category]})</span>
          )}
        </Button>
      ))}
    </div>
  );
}

function ItemsGrid({
  items,
  isLoading,
  error,
  searchQuery,
  selectedCategory,
  onItemClick,
  onRefetch,
}: {
  readonly items: CatalogItem[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly searchQuery: string;
  readonly selectedCategory: string;
  readonly onItemClick: (item: CatalogItem) => void;
  readonly onRefetch: () => void;
}) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`skeleton-${i.toString()}`} className="h-56 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Package className="mb-4 h-12 w-12 text-red-500" />
          <p className="text-lg font-medium">Failed to load catalog</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'An error occurred'}
          </p>
          <Button variant="outline" className="mt-4" onClick={onRefetch}>Try Again</Button>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    const hasFilters = Boolean(searchQuery) || selectedCategory !== 'All';
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Package className="mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-lg font-medium">No items found</p>
          <p className="text-sm text-muted-foreground">
            {hasFilters ? 'Try adjusting your filters' : 'Add items to your catalog to get started'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <CatalogItemCard
          key={item.itemId}
          item={item}
          onClick={() => onItemClick(item)}
        />
      ))}
    </div>
  );
}
